import { Badge, Button, Chip, IconNode, ToolTip, Typography } from '@components/base';
import css from './index.module.scss';
import { useEffect, useState } from 'react';
import {
  calculateSavedQuoteCMValues,
  getCommaSeperatedCurrencyAmount,
  getFormattedDate,
  getFormattedTime,
  sanitizeValue
} from '@helpers/utils';
import { useNavigate } from 'react-router-dom';
import AddEditQuote from '@pages/quote/quote-detail/components/add-edit-quote';
import { CURRENCY, ENQUIRY_TYPE, MARKET_TYPE, WEIGHT_SYSTEM } from '@helpers/constants';
import ViewQuoteModal from '@pages/quote/quote-detail/components/view-quote';
import { DeletePrompt, LinkButton } from '@components/common';
import { endOfDay, format } from 'date-fns';
import notify from '@helpers/toastify-helper';
import {
  getEnquiryDetails,
  submitConsolidationQuote,
  submitNonConsolidationQuote
} from '@services/quote.service';
import Images from '@assets/images';
import { IWeightChildOption } from '@helpers/types/quotes';
import QuoteFormWrapper from '@components/common/quote-wrapper';
import SaveToDraftPrompt from '@components/common/quote-wrapper/components/save-to-draft-confirmation-modal';
import CMValue from '@components/common/quote-wrapper/components/cm-value';

const DEAL_NAME_MAX_LENGTH = 50;

const EnquiryCard = (props: any) => {
  const { data, fetchEnquiryList } = props;
  const navigate = useNavigate();

  const [enquiryCardState, setEnquiryCardState] = useState<any>({
    detailView: false,
    showAddQuote: false,
    isConsolidation: false,
    quoteDetails: null,
    addQuoteProductData: null,
    showViewQuote: false,
    viewQuoteData: null,
    showDisclaimer: false,
    primaryUnit: null,
    showQuoteForm: false,
    quoteFormData: null,
    isDomestic: data.market_type === MARKET_TYPE.DOMESTIC ? true : false,
    showDraftDisclaimer: false,
    draftReqBody: null
  });

  const {
    detailView,
    showAddQuote,
    isConsolidation,
    quoteDetails,
    addQuoteProductData,
    showViewQuote,
    viewQuoteData,
    showDisclaimer,
    primaryUnit,
    showQuoteForm,
    quoteFormData,
    isDomestic,
    showDraftDisclaimer,
    draftReqBody
  } = enquiryCardState;

  useEffect(() => {
    setEnquiryCardState((prevState: any) => {
      const weightSystem = WEIGHT_SYSTEM.find((item) => item.value === data.weight_system);
      const primaryUnitValue = weightSystem?.childOptions?.find(
        (item: IWeightChildOption) => item.value === data.primary_unit
      );
      return {
        ...prevState,
        isConsolidation: data?.is_consolidation,
        quoteDetails: {
          currency1: CURRENCY.find((item) => item.value === data.currency_type_one),
          currency2: CURRENCY.find((item) => item.value === data.currency_type_two),
          fxRate: data.fx_rate,
          selectedWeightSystemChildOptions: weightSystem?.childOptions || null,
          selectedCurrency: [
            CURRENCY.find((item) => item.value === data.currency_type_one),
            CURRENCY.find((item) => item.value === data.currency_type_two)
          ],
          primaryUnit: primaryUnitValue
        },
        primaryUnit: primaryUnitValue
      };
    });
  }, [data]);

  const cm2Context = {
    currency1: CURRENCY.find((item) => item.value === data.currency_type_one) ?? null,
    fxRate: data.fx_rate,
    primaryUnit,
    isDomestic
  };

  // For consolidation the latest quote carries the consolidation fields (incoterm, finance cost)
  const getFormattedCM = (cm: 'cm1' | 'cm2', latestQuote: any) => {
    const cmValues = calculateSavedQuoteCMValues(
      latestQuote,
      cm2Context,
      isConsolidation ? latestQuote : undefined
    );
    return cm === 'cm1' ? (
      <CMValue
        amount={cmValues.cm1}
        percent={cmValues.cm1Percent}
        currency={cm2Context.currency1?.label}
      />
    ) : (
      <CMValue
        amount={cmValues.cm2}
        percent={cmValues.cm2Percent}
        currency={cm2Context.currency1?.label}
      />
    );
  };

  const transformData = (data: any) => {
    const firstQuote = data[0].latest_quote;
    const transformed: any = {
      consolidation_details_id: firstQuote.consolidation_id || '',
      total_weight: firstQuote.total_weight || '',
      total_weight_unit: firstQuote.total_weight_unit || '',
      total_local_freight: firstQuote.total_local_freight || {},
      total_port_and_clearance_expenses: firstQuote.total_port_and_clearance_expenses || {},
      total_freight: firstQuote.total_freight || {},
      pol: firstQuote.pol || {},
      pod: firstQuote.pod || {},
      insurance_cost: firstQuote.insurance_cost || null,
      additional_cost: firstQuote.additional_cost || {},
      discount: firstQuote.discount || null,
      deal_incoterm: firstQuote.deal_incoterm || '',
      deal_incoterm_location: firstQuote.deal_incoterm_location || '',
      finance_cost: firstQuote.finance_cost || null,
      switch_bl_cost: firstQuote.switch_bl_cost || {}
    };
    transformed.products = data.map((item: any) => {
      const quote = item.latest_quote;
      return {
        quote_id: quote.consolidation_id,
        product_data: {
          name: item.product_data.product_name,
          id: item.product_data.product_id,
          cpd_product_id: item.product_data.cpd_product_id ?? null
        },
        quantity: Number(quote.quantity) || 0,
        quantity_unit: quote.quantity_unit || '',
        moq: Number(quote.moq) || 0,
        moq_unit: quote.moq_unit || '',
        base_price: quote.base_price || {},
        ex_works_price: quote.ex_works_price || {},
        fob_price: quote.fob_price || {},
        cfr_price: quote.cfr_price || {},
        cif_price: quote.cif_price || {},
        final_price: quote.final_price || {},
        final_price_weight_unit: quote?.final_price_weight_unit,
        profit_margin: Number(quote.profit_margin) || 0,
        additional_cost: quote.additional_cost || {},
        packaging: quote.packaging || '',
        origin: quote.origin || '',
        origin_display_value: quote.origin_display_value ?? null,
        material_location_country: quote.material_location_country ?? null,
        material_location_country_display_value:
          quote.material_location_country_display_value ?? null
      };
    });
    return transformed;
  };

  const openViewQuoteModal = (product: any) => {
    const productData = isConsolidation
      ? transformData(product)
      : {
          ...product.latest_quote,
          product_name: product.product_data?.product_name,
          product_id: product.product_data?.product_id,
          cpd_product_id: product.product_data?.cpd_product_id ?? null
        };
    setEnquiryCardState(() => {
      return {
        ...enquiryCardState,
        showViewQuote: true,
        viewQuoteData: productData
      };
    });
  };

  const closeViewQuoteModal = () => {
    setEnquiryCardState(() => {
      return {
        ...enquiryCardState,
        showViewQuote: false
      };
    });
  };

  const getFormattedDateWithoutYear = (date: any) => {
    if (date) {
      return format(endOfDay(new Date(date).getTime()), 'do LLL');
    } else {
      return null;
    }
  };

  const fetchEnquiryDetail = async (enquiry_id: string) => {
    const response = await getEnquiryDetails(enquiry_id);
    if (response?.success) {
      const { data } = response;
      setEnquiryCardState(() => {
        return {
          ...enquiryCardState,
          showQuoteForm: true,
          quoteFormData: data
        };
      });
    } else if (response?.error) {
      notify({
        severity: 'error',
        message: 'Something Went Wrong' // response?.error?.message
      });
    }
  };

  const saveAsDraft = async () => {
    if (!draftReqBody) {
      notify({
        severity: 'error',
        message: 'No form data available to save'
      });
      return;
    }
    const response = isConsolidation
      ? await submitConsolidationQuote(draftReqBody)
      : await submitNonConsolidationQuote(draftReqBody);

    if (response.success) {
      notify({
        severity: 'success',
        message: response.data.message
      });
      setEnquiryCardState((prevState: any) => ({
        ...prevState,
        showDraftDisclaimer: false,
        showQuoteForm: false
      }));
      navigate('/quote?activeTab=cost_calculator');
    } else {
      notify({
        severity: 'error',
        message: `Something Went Wrong!!`
      });
    }
  };

  const onClose = (data: any) => {
    setEnquiryCardState(() => {
      return {
        ...enquiryCardState,
        showDraftDisclaimer: true,
        draftReqBody: data
      };
    });
  };

  // Helper function to format incoterm location for better reusability
  const formatIncoTermLocation = (product: any) => {
    const incoTerm = product?.latest_quote?.deal_incoterm;
    let incoTermLocation = '';

    if (isDomestic) {
      incoTermLocation = product?.latest_quote?.deal_incoterm_location;
    } else {
      incoTermLocation = incoTerm + ' - ';
      switch (incoTerm) {
        case 'EXW':
        case 'DDP':
        case 'DAP':
          incoTermLocation += product?.latest_quote?.deal_incoterm_location;
          break;
        case 'FOB':
          incoTermLocation += product?.latest_quote?.pol?.name;
          break;
        case 'CIF':
        case 'CNF':
        case 'CFR':
          incoTermLocation += product?.latest_quote?.pod?.name;
          break;
        default:
          incoTermLocation += product?.latest_quote?.pod?.name;
          break;
      }
    }
    return incoTermLocation;
  };

  return (
    <main className={css.mainWrapper}>
      <div className={css.EnquiryHeader}>
        <div className={css.EnquiryHeaderText}>
          <div className={css.keyValue}>
            <Typography variant="label">Enquiry Number</Typography>
            <Typography variant="h5">{sanitizeValue(data?.readable_enquiry_number)}</Typography>
          </div>
          <div className={css.keyValue}>
            <Typography variant="label">Date</Typography>
            <Typography variant="body">
              {sanitizeValue(getFormattedDate(data?.created_at), '-')}
            </Typography>
          </div>
          <div className={css.keyValue}>
            <Typography variant="label">Customer</Typography>
            <Typography variant="h5">{sanitizeValue(data?.customer_name)}</Typography>
          </div>
          <div className={css.keyValue}>
            <Typography variant="label">Deal Name</Typography>
            {data?.deal_name?.length > DEAL_NAME_MAX_LENGTH ? (
              <ToolTip title={data.deal_name} placement="top">
                <div>
                  <Typography variant="body">
                    {sanitizeValue(data.deal_name.substring(0, DEAL_NAME_MAX_LENGTH) + '...')}
                  </Typography>
                </div>
              </ToolTip>
            ) : (
              <Typography variant="body">{sanitizeValue(data.deal_name)}</Typography>
            )}
          </div>
          <div className={css.keyValue}>
            <Typography variant="label">Created By</Typography>
            <Typography variant="h5">
              {sanitizeValue(data?.created_by?.given_name + ' ' + data?.created_by?.family_name)}
            </Typography>
          </div>
          <div className={css.filterStates}>
            <Chip className={css.chips} variant="filled-blue" label={data.container_type} />
            <Chip
              className={css.chips}
              variant="filled-yellow"
              label={data.sourcing_type == 'MTT' ? 'MTT' : 'Non-MTT'}
            />
            <Chip
              className={css.chips}
              variant="filled-green"
              label={data.is_consolidation ? 'Consolidation' : 'Non-Consolidation'}
            />
          </div>
        </div>
        <div className={css.EnquiryHeaderButton}>
          <ToolTip title="Duplicate this Enquiry" placement="left">
            <div>
              <IconNode
                src={Images.duplicateRed}
                alt="Duplicate Enquiry"
                className={css.editIcon}
                onClick={() => {
                  fetchEnquiryDetail(data.enquiry_id);
                }}
              />
            </div>
          </ToolTip>
          <LinkButton
            variant="outlined-secondary"
            to={`/quote/detail/${data.enquiry_id}`}
            title="View Enquiry"
          >
            View Enquiry
          </LinkButton>
        </div>
      </div>
      <div className={css.enquiryCardDivider}>
        <div className={css.arrowIconWrapper}>
          {detailView ? (
            <div
              className={css.badgeWrapper}
              onClick={() =>
                setEnquiryCardState(() => {
                  return {
                    ...enquiryCardState,
                    detailView: false
                  };
                })
              }
            >
              <Badge className={css.badge}>Close</Badge>
            </div>
          ) : (
            <div
              className={css.badgeWrapper}
              onClick={() =>
                setEnquiryCardState(() => {
                  return {
                    ...enquiryCardState,
                    detailView: true
                  };
                })
              }
            >
              <Badge className={css.badge}>
                {data.product_details?.length > 1 ? 'View Products' : 'View Product'}
              </Badge>
            </div>
          )}
        </div>
        <div className={css.marketBadgeWrapper}>
          <Chip
            variant={data.market_type === MARKET_TYPE.DOMESTIC ? 'filled-green' : 'filled-blue'}
            label={data.market_type === MARKET_TYPE.DOMESTIC ? 'Domestic' : 'International'}
            className={css.marketBadge}
          />
        </div>
      </div>
      {detailView &&
        (isConsolidation
          ? (() => {
              const groupedProducts = data.product_details.reduce((acc: any, product: any) => {
                const { consolidation_id } = product.latest_quote;
                if (!acc[consolidation_id]) {
                  acc[consolidation_id] = [];
                }
                acc[consolidation_id].push(product);
                return acc;
              }, {});

              return Object.keys(groupedProducts).map((consolidationId, consolidationIndex) => (
                <div key={consolidationIndex} className={css.ConsolidationProductWrapper}>
                  <div className={css.ConsolidationHeader}>
                    <Typography variant="h5">Consolidation {consolidationIndex + 1}</Typography>
                    <div className={css.buttonWrapper}>
                      <Button
                        variant="contained"
                        onClick={() => {
                          setEnquiryCardState((prevState: any) => {
                            const productData = {
                              products: groupedProducts[consolidationId].map((item: any) => ({
                                product_data: {
                                  name: item.product_data?.product_name,
                                  id: item.product_data?.product_id,
                                  cpd_product_id: item.product_data?.cpd_product_id ?? null
                                }
                              }))
                            };
                            return {
                              ...prevState,
                              showAddQuote: true,
                              addQuoteProductData: {
                                ...productData,
                                consolidation_id: consolidationId
                              }
                            };
                          });
                        }}
                      >
                        Add Quote
                      </Button>
                      <Button
                        variant="outlined-secondary"
                        onClick={() => {
                          openViewQuoteModal(groupedProducts[consolidationId]);
                        }}
                      >
                        View Quote
                      </Button>
                    </div>
                  </div>

                  {groupedProducts[consolidationId].map((product: any, index: number) => {
                    const incoTermLocation = formatIncoTermLocation(product);

                    return (
                      <div key={index} className={css.consolidationProductDetails}>
                        <div className={css.ProductDetails}>
                          <div className={css.keyValue}>
                            <div className={css.productName}>
                              {sanitizeValue(
                                product.product_data?.product_name,
                                `Product ${index + 1}`
                              )}
                            </div>
                            <div className={css.dateCaption}>
                              {sanitizeValue(
                                getFormattedDateWithoutYear(product?.latest_quote?.created_at)
                              )}{' '}
                              {getFormattedTime(product?.latest_quote?.created_at)}
                            </div>
                          </div>
                          <div className={css.keyValue}>
                            <Typography variant="label">
                              Quoted Amounts{' '}
                              <span>
                                ( Per{' '}
                                {product?.latest_quote?.final_price.unit_of_weight ||
                                  primaryUnit?.label}{' '}
                                )
                              </span>
                            </Typography>
                            <Typography variant="h5">
                              {product?.latest_quote?.final_price?.currency
                                ? getCommaSeperatedCurrencyAmount(
                                    product?.latest_quote?.final_price?.currency,
                                    product?.latest_quote?.final_price?.value
                                  )
                                : '-'}
                            </Typography>
                          </div>
                          <div className={css.keyValue}>
                            <Typography variant="label">Expected CM1</Typography>
                            <Typography variant="h5" className={css.cmValue}>
                              {getFormattedCM('cm1', product?.latest_quote)}
                            </Typography>
                          </div>
                          <div className={css.keyValue}>
                            <Typography variant="label">Expected CM2</Typography>
                            <Typography variant="h5" className={css.cmValue}>
                              {getFormattedCM('cm2', product?.latest_quote)}
                            </Typography>
                          </div>
                          <div className={css.keyValue} title={sanitizeValue(incoTermLocation)}>
                            <Typography variant="label">
                              {isDomestic ? 'Place of Discharge' : 'Inco Terms'}
                            </Typography>
                            <Typography variant="body" className={css.truncateText}>
                              {sanitizeValue(incoTermLocation)}
                            </Typography>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ));
            })()
          : (data.product_details || []).map((product: any, index: number) => {
              const incoTermLocation = formatIncoTermLocation(product);

              return (
                <div key={index} className={css.ProductWrapper}>
                  <div className={css.ProductDetails}>
                    <div className={css.keyValue}>
                      <div className={css.productName}>
                        {sanitizeValue(product.product_data?.product_name, `Product ${index + 1}`)}
                      </div>
                      <div className={css.dateCaption}>
                        {sanitizeValue(
                          getFormattedDateWithoutYear(product?.latest_quote?.created_at)
                        )}{' '}
                        {getFormattedTime(product?.latest_quote?.created_at)}
                      </div>
                    </div>
                    <div className={css.keyValue}>
                      <Typography variant="label">
                        Quoted Amount{' '}
                        <span>
                          ( Per{' '}
                          {product?.latest_quote?.final_price.unit_of_weight || primaryUnit?.label}{' '}
                          )
                        </span>
                      </Typography>
                      <Typography variant="h5">
                        {product?.latest_quote?.final_price?.currency
                          ? getCommaSeperatedCurrencyAmount(
                              product?.latest_quote?.final_price?.currency,
                              product?.latest_quote?.final_price?.value
                            )
                          : '-'}
                      </Typography>
                    </div>
                    <div className={css.keyValue}>
                      <Typography variant="label">Expected CM1</Typography>
                      <Typography variant="h5" className={css.cmValue}>
                        {getFormattedCM('cm1', product?.latest_quote)}
                      </Typography>
                    </div>
                    <div className={css.keyValue}>
                      <Typography variant="label">Expected CM2</Typography>
                      <Typography variant="h5" className={css.cmValue}>
                        {getFormattedCM('cm2', product?.latest_quote)}
                      </Typography>
                    </div>
                    <div className={css.keyValue} title={sanitizeValue(incoTermLocation)}>
                      <Typography variant="label">
                        {isDomestic ? 'Place of Discharge' : 'Inco Terms'}
                      </Typography>
                      <Typography variant="body" className={css.truncateText}>
                        {sanitizeValue(incoTermLocation)}
                      </Typography>
                    </div>
                  </div>
                  <div className={css.ProductView}>
                    <Button
                      variant="contained"
                      onClick={() => {
                        setEnquiryCardState(() => {
                          return {
                            ...enquiryCardState,
                            showAddQuote: true,
                            addQuoteProductData: {
                              ...product
                            }
                          };
                        });
                      }}
                    >
                      Add Quote
                    </Button>
                    <Button
                      variant="text"
                      onClick={() => {
                        openViewQuoteModal(product);
                      }}
                    >
                      View
                    </Button>
                  </div>
                </div>
              );
            }))}

      {showAddQuote && (
        <AddEditQuote
          open={showAddQuote}
          onClose={() => {
            setEnquiryCardState(() => {
              return {
                ...enquiryCardState,
                showDisclaimer: true
              };
            });
          }}
          onSubmitClose={() => {
            setEnquiryCardState(() => {
              return {
                ...enquiryCardState,
                showAddQuote: false
              };
            });
            fetchEnquiryList && fetchEnquiryList();
          }}
          isConsolidation={isConsolidation}
          isFcl={data.container_type == 'FCL'}
          isMtt={data.sourcing_type == 'MTT'}
          quoteDetail={quoteDetails}
          isEdit={'add'}
          enquiryId={data.enquiry_id}
          data={addQuoteProductData}
          enquiryType={isDomestic ? ENQUIRY_TYPE.DOMESTIC : ENQUIRY_TYPE.INTERNATIONAL}
        />
      )}

      {showViewQuote && (
        <ViewQuoteModal
          open={showViewQuote}
          onClose={closeViewQuoteModal}
          data={viewQuoteData}
          cm2Context={cm2Context}
          isConsolidation={isConsolidation}
          primaryUnit={primaryUnit ? primaryUnit?.value : ''}
        />
      )}

      {showDisclaimer && (
        <DeletePrompt
          open={showDisclaimer}
          onClose={() =>
            setEnquiryCardState((prevState: any) => ({
              ...prevState,
              showDisclaimer: false
            }))
          }
          onConfirm={() =>
            setEnquiryCardState((prevState: any) => ({
              ...prevState,
              showDisclaimer: false,
              showAddQuote: false,
              showQuoteForm: false
            }))
          }
          message="Are you sure you want to close?"
        />
      )}

      {showDraftDisclaimer && (
        <SaveToDraftPrompt
          open={showDraftDisclaimer}
          onClose={() =>
            setEnquiryCardState((prevState: any) => ({
              ...prevState,
              showDraftDisclaimer: false
            }))
          }
          onConfirm={() => {
            setEnquiryCardState((prevState: any) => ({
              ...prevState,
              showDraftDisclaimer: false,
              showQuoteForm: false
            }));
          }}
          onSaveAsDraft={saveAsDraft}
          message="Are you sure you want to close?"
        />
      )}

      {showQuoteForm && (
        <QuoteFormWrapper
          showQuoteForm={showQuoteForm}
          onClose={onClose}
          onSubmitClose={() => {
            setEnquiryCardState(() => {
              return {
                ...enquiryCardState,
                showDisclaimer: false,
                quoteFormData: null,
                showQuoteForm: false
              };
            });
          }}
          isFcl={data.container_type == 'FCL'}
          isMtt={data.sourcing_type == 'MTT'}
          isConsolidation={isConsolidation}
          data={quoteFormData}
          isDuplicate={true}
          isDomestic={isDomestic}
        />
      )}
    </main>
  );
};

export default EnquiryCard;
